import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Parallax } from "react-scroll-parallax";
import useGet from "../hooks/useGet";
import SEO from "../components/shared/SEO";
import BlockAircraft from "../components/landing/BlockAircraft";
import BlockArticles from "../components/landing/BlockArticles";
import BlockFAQs from "../components/landing/BlockFAQs";
import BlockHero from "../components/landing/BlockHero";
import BlockText from "../components/landing/BlockText";
import BlockTextAndImage from "../components/landing/BlockTextAndImage";
import Modal from "../components/ui/Modal";
import ServiceRequest from "../components/landing/ServiceRequest";
import QuoteQueryJets from "../components/shared/QuoteQueryJets";
import getClassName from "../utilities/getClassName";
import { getUrl } from "../utilities/url";
import PoweredByContext from "../store/PoweredByContext";
import LazyLoadingContext from "../store/LazyLoadingContext";

const LandingPage = ({ showModalOnLoad = false }) => {
    const pageSlug = useParams().page;
    const { error, returnData: page } = useGet(`/api/landingpages/v2?path=/${pageSlug}`);
    const [showModal, setShowModal] = useState(showModalOnLoad);
    const navigate = useNavigate();

    const poweredByCtx = useContext(PoweredByContext);
    const lazyLoadingCtx = useContext(LazyLoadingContext);

    useEffect(() => {
        return () => {
            lazyLoadingCtx.reset("landingPage");
        };
        // Intentionally mount-only: re-firing would call reset() and clobber
        // page-ready state that other effects have already populated.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (page !== false && page !== "") {
            lazyLoadingCtx.updatePageCheck("landingPage", {
                landingpages: true,
            });
        }
    }, [page, lazyLoadingCtx]);

    useEffect(() => {
        setShowModal(showModalOnLoad);
    }, [showModalOnLoad]);

    const usePrevious = (showModal) => {
        const prev = useRef();

        useEffect(() => {
            prev.current = showModal;
        }, [showModal]);

        return prev.current;
    };

    const prevShowModal = usePrevious(showModal);

    useEffect(() => {
        if (showModal === false && prevShowModal === true) {
            let pathParts = window.location.pathname.split("/");

            const destination = pathParts
                .filter((part, i) => {
                    return part !== "request" && i !== pathParts.length - 1;
                })
                .join("/");

            navigate(destination);
        }
    }, [showModal, prevShowModal, navigate]);

    useEffect(() => {
        if (error) {
            navigate("/");
        }
    }, [error, navigate]);

    const getHeroService = () => {
        let service = null;

        page?.contentBlocks?.forEach((block) => {
            if (block.blockName === "hero") {
                service = block.props.service;
            }
        });

        return service;
    };

    const getBlockComponent = (name) => {
        if (name === "aircraftCarousel") {
            return BlockAircraft;
        } else if (name === "relatedArticles") {
            return BlockArticles;
        } else if (name === "faq") {
            return BlockFAQs;
        } else if (name === "hero") {
            return BlockHero;
        } else if (name === "textWithSection") {
            return BlockText;
        } else if (name === "textWithCta") {
            return BlockTextAndImage;
        } else if (name === "testimonial") {
            return BlockTextAndImage;
        }
    };

    const getTestimonials = (block) => {
        let testimonials = [];

        block.props.contentBlocks.forEach((item) => {
            testimonials.push({
                text: item.props.text,
                author: item.props.author,
            });
        });

        return testimonials;
    };

    const getFAQs = (block) => {
        let faqs = [];

        if (block.props.contentBlocks) {
            block.props.contentBlocks.forEach((item) => {
                faqs.push({
                    question: item.props.faq,
                    answer: item.props.answerFAQ,
                });
            });
        }

        return faqs;
    };

    const getBlockProps = (block) => {
        let props = {};
        if (block.blockName === "aircraftCarousel") {
            return Object.assign(props, {
                title: block.props.title,
                subtitle: block.props.subTitle || block.props.subtitle,
                items: block.props.contentBlocks,
                ctaUrl: block.props.links[0] ? block.props.links[0].url : undefined,
                ctaText: block.props.links[0] ? block.props.links[0].name : undefined,
                mainHeading: block.props.mainHeading,
                subHeading: block.props.subHeading,
            });
        } else if (block.blockName === "faq") {
            return Object.assign(props, {
                title: block.props.title,
                subtitle: block.props.subTitle || block.props.subtitle,
                text: block.props.text,
                showMoreText: block.props.showMoreText,
                items: getFAQs(block),
                ctaUrl: block.props.links[0] ? block.props.links[0].url : undefined,
                ctaText: block.props.links[0] ? block.props.links[0].name : undefined,
                mainHeading: block.props.mainHeading,
                subHeading: block.props.subHeading,
            });
        } else if (block.blockName === "textWithSection") {
            return Object.assign(props, {
                textBlocks: block.props.contentBlocks ? block.props.contentBlocks : [],
                ctaUrl: block.props.links && block.props.links[0] ? block.props.links[0].url : undefined,
                ctaText: block.props.links && block.props.links[0] ? block.props.links[0].name : undefined,
            });
        } else if (block.blockName === "textWithCta") {
            const defaultProps = Object.assign(props, block.props);

            return Object.assign(defaultProps, {
                subtitle: block.props.subTitle || block.props.subtitle,
                ctaUrl: block.props.links[0] ? block.props.links[0].url : undefined,
                ctaText: block.props.links[0] ? block.props.links[0].name : undefined,
            });
        } else if (block.blockName === "testimonial") {
            const defaultProps = Object.assign(props, block.props);

            return Object.assign(defaultProps, {
                subtitle: block.props.subTitle || block.props.subtitle,
                testimonials: getTestimonials(block),
            });
        } else {
            return Object.assign(props, block.props);
        }
    };

    const [animate, setAnimate] = useState([]);

    // Animate In Out with parallax
    const animateInOutHandler = (progress, percent, animRef) => {
        if (progress > percent && !animate[animRef]) {
            const updateAnim = { ...animate, [animRef]: true };
            setAnimate(updateAnim);
        }

        if (progress === 0 && animate[animRef]) {
            const updateAnim = { ...animate, [animRef]: false };
            setAnimate(updateAnim);
        }
    };

    const canShowModal = () => {
        return getHeroService() === "Helicopters" || getHeroService() === "Group Charter";
    };

    return (
        <>
            {page && (
                <SEO
                    title={page.metaTitle}
                    description={page.metaDescription}
                    canonicalUrl={getUrl(poweredByCtx, window.location.pathname)}
                />
            )}
            <div className="landing-page">
                {lazyLoadingCtx.pageReady && page && poweredByCtx.siteReady ? (
                    <div className="landing-page__content">
                        {page.stickyQuoteBool && (
                            <div className="wrap">
                                <QuoteQueryJets onPage />
                            </div>
                        )}
                        {page.contentBlocks.map((block, i) => {
                            const Component = getBlockComponent(block.blockName);

                            if (
                                Component &&
                                !(
                                    poweredByCtx.isPoweredBy &&
                                    (block.blockName === "testimonial" || block.blockName === "relatedArticles")
                                )
                            ) {
                                return (
                                    <div key={i}>
                                        <Parallax
                                            onProgressChange={(progress) => {
                                                animateInOutHandler(progress, 0.15, `block_${i}`);
                                            }}
                                            className={getClassName([
                                                "fade-and-travel",
                                                `parallax-block-${block.blockName}`,
                                                animate[`block_${i}`] && "fade-and-travel--visible",
                                            ])}
                                        >
                                            <Component {...getBlockProps(block)} />
                                        </Parallax>
                                    </div>
                                );
                            }

                            return null;
                        })}
                    </div>
                ) : (
                    <>
                        <div className="wrap">
                            <QuoteQueryJets onPage skeleton={true} />
                        </div>
                        <BlockTextAndImage
                            title="skeleton"
                            subtitle="skeleton"
                            text="<p>skeleton</p><p>skeleton</p><p>skeleton</p>"
                            skeleton={true}
                        />
                    </>
                )}
            </div>
            {canShowModal() && (
                <Modal
                    modalVisible={showModal}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                >
                    <ServiceRequest service={getHeroService()} />
                </Modal>
            )}
        </>
    );
};

export default LandingPage;
